import { useCallback, useMemo } from "react";
import { useNavbar } from "../navbar.context";
import { isTouchscreen } from "../../../utils/device";
import { useRovingFocus, useKeypress, useOutsideClick, useWindowResize } from "../../../hooks";

interface UseNavbarMenuProps {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentRef: React.RefObject<HTMLUListElement | null>;
  portalRef?: React.RefObject<HTMLDivElement | null>;
  id: string;
  onPointerLeave?: (event: React.PointerEvent<HTMLLIElement>) => void;
}

export function useNavbarMenu(props: UseNavbarMenuProps) {
  const { triggerRef, contentRef, portalRef, id, onPointerLeave } = props;

  const { value, toggleMenu } = useNavbar();

  const isOpen = useMemo(() => value === id, [value, id]);

  const getMenuPosition = useCallback(() => {
    if (!triggerRef.current || !portalRef?.current || !contentRef.current) return;

    const offset = triggerRef.current.offsetLeft;
    const height = contentRef.current.scrollHeight;

    return { x: offset, height };
  }, [portalRef]);

  const setMenuPosition = useCallback(() => {
    const pos = getMenuPosition();

    if (!portalRef?.current || !pos) return;

    portalRef.current.style.transform = `translateX(${pos.x}px)`;
  }, [getMenuPosition, portalRef]);

  const handleToggleMenu = useCallback(() => {
    toggleMenu(id);
  }, [toggleMenu, id]);

  const openOnPointerEnter = useCallback(() => {
    if (isTouchscreen() || isOpen) return;

    toggleMenu(id);
  }, [toggleMenu, id, isOpen]);

  const closeOnPointerLeave = useCallback(
    (event: React.PointerEvent<HTMLLIElement>) => {
      onPointerLeave?.(event);

      if (!isOpen) return;

      toggleMenu(null);
    },
    [toggleMenu, onPointerLeave, isOpen],
  );

  const closeOnOutsideClick = useCallback(() => {
    toggleMenu(null);
  }, [toggleMenu]);

  const onHorizontalArrowPress = useRovingFocus({ orientation: "horizontal" });

  const handleHorizontalArrowPress = useCallback(
    (event: React.KeyboardEvent) => {
      onHorizontalArrowPress(event);
    },
    [onHorizontalArrowPress],
  );

  const moveFocusToMenuIfOpen = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      const firstFocusableElement = contentRef.current?.querySelector("button, a");

      if (firstFocusableElement) {
        event.preventDefault();
        event.stopPropagation();
        (firstFocusableElement as HTMLElement).focus();
      }
    },
    [isOpen],
  );

  const handleMenuClose = useCallback(() => {
    if (!isOpen) return;

    toggleMenu(null);
    triggerRef.current?.focus();
  }, [toggleMenu, isOpen]);

  useKeypress("ArrowDown", moveFocusToMenuIfOpen);

  useKeypress("Escape", handleMenuClose);

  useOutsideClick({
    ref: portalRef,
    callback: closeOnOutsideClick,
    active: isOpen,
  });

  useWindowResize({
    callback: setMenuPosition,
    active: isOpen,
  });

  return {
    getMenuPosition,
    setMenuPosition,
    handleToggleMenu,
    openOnPointerEnter,
    closeOnPointerLeave,
    closeOnOutsideClick,
    handleHorizontalArrowPress,
    moveFocusToMenuIfOpen,
    handleMenuClose,
  };
}
